import type { FC, ReactNode } from 'react'
import type { TocItem } from '@/types'

import { useEffect, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TableOfContentsProps {
    items: Array<TocItem>
    title?: string
    className?: string
}

const useActiveHeading = (idKey: string): string | null => {
    const [active, setActive] = useState<string | null>(null)

    useEffect(() => {
        const ids = idKey ? idKey.split('|') : []

        if (ids.length === 0 || typeof IntersectionObserver === 'undefined') return

        const visible = new Map<string, boolean>()
        const headings = ids
            .map((id) => document.getElementById(id))
            .filter((element): element is HTMLElement => element !== null)

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => visible.set(entry.target.id, entry.isIntersecting))

            const current = ids.find((id) => visible.get(id))

            if (current) setActive(current)
        }, { rootMargin: '-88px 0px -60% 0px', threshold: [0, 1] })

        headings.forEach((heading) => observer.observe(heading))

        return () => observer.disconnect()
    }, [idKey])

    return active
}

const TocList: FC<{ items: Array<TocItem>, active: string | null }> = ({ items, active }): ReactNode => {
    return (
        <ol className='flex flex-col'>
            {items.map((item) => {
                const isActive = item.id === active

                return (
                    <li key={item.id}>
                        <a
                            href={`#${item.id}`}
                            aria-current={isActive ? 'location' : undefined}
                            className={cn(
                                'group/toc relative flex gap-3 border-l py-1.5 text-[0.875rem] leading-snug transition-colors duration-200',
                                item.depth === 3 ? 'pl-7' : 'pl-4',
                                isActive
                                    ? 'border-brand text-foreground'
                                    : 'border-border text-muted-foreground hover:border-border-strong hover:text-foreground'
                            )}
                        >
                            {item.text}
                        </a>
                    </li>
                )
            })}
        </ol>
    )
}

/**
 * Sticky "On this page" rail on desktop that tracks the section in view,
 * and a collapsible list on smaller screens (works without JavaScript).
 */
const TableOfContents: FC<TableOfContentsProps> = ({ items, title = 'On this page', className }): ReactNode => {
    const active = useActiveHeading(items.map((item) => item.id).join('|'))

    if (items.length === 0) return null

    return (
        <div className={className}>
            <details className='group/details border-y border-border lg:hidden'>
                <summary className='flex min-h-12 cursor-pointer list-none items-center justify-between gap-4 font-mono text-label uppercase text-muted-foreground [&::-webkit-details-marker]:hidden'>
                    {title}
                    <ChevronDown aria-hidden='true' className='size-4 transition-transform duration-200 group-open/details:rotate-180' />
                </summary>

                <nav aria-label={title} className='pb-4'>
                    <TocList items={items} active={active} />
                </nav>
            </details>

            <nav aria-label={title} className='hidden lg:block'>
                <p className='mb-4 font-mono text-label uppercase text-muted-foreground'>
                    {title}
                </p>
                <TocList items={items} active={active} />
            </nav>
        </div>
    )
}

export default TableOfContents
